/**
 * API client for KCS Backend
 * Handles authentication, request formatting, and error handling.
 *
 * Security Notes:
 * - Tokens stored in localStorage (acceptable for SPAs, aware of XSS risk)
 * - CSRF: Django REST Framework with JWT authentication is exempt from CSRF
 *   as per Django's CSRF middleware documentation when using session-less auth
 */

export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api'

// Request timeout in milliseconds (60 seconds)
const REQUEST_TIMEOUT = 60000

// ETag cache for conditional requests
interface CacheEntry {
  etag: string
  data: unknown
  timestamp: number
}

const etagCache = new Map<string, CacheEntry>()
const CACHE_MAX_AGE = 5 * 60 * 1000 // 5 minutes

// Cleanup old cache entries periodically
setInterval(() => {
  const now = Date.now()
  for (const [key, entry] of etagCache.entries()) {
    if (now - entry.timestamp > CACHE_MAX_AGE) {
      etagCache.delete(key)
    }
  }
}, 60 * 1000) // Every minute

interface ApiError extends Error {
  status: number
  data?: unknown
}

interface FetchOptions extends RequestInit {
  __retryCount?: number
}

/**
 * Fetch wrapper with authentication, timeout, and error handling
 */
export async function fetchApi<T>(endpoint: string, options?: FetchOptions): Promise<T> {
  // Validate endpoint to prevent open redirect
  if (!endpoint.startsWith('/')) {
    throw new Error(`Invalid endpoint: must start with /`)
  }

  const token = localStorage.getItem('kcs_access_token')
  const retryCount = options?.__retryCount ?? 0

  // Create abort controller for timeout
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT)

  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      signal: controller.signal,
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options?.headers,
      },
    })

    clearTimeout(timeoutId)

    if (!response.ok) {
      const error = new Error(`HTTP ${response.status}`) as ApiError
      error.status = response.status

      // Try to parse error response
      try {
        const data = await response.json()
        error.data = data
        if (data && typeof data.detail === 'string') {
          error.message = data.detail
        } else if (data && typeof data.error === 'string') {
          error.message = data.error
        }
      } catch {
        // Response not JSON, ignore
      }

      // Handle token expiration - limit to 1 retry to prevent infinite loop
      if (response.status === 401 && retryCount < 1) {
        const refreshed = await tryRefreshToken()
        if (refreshed) {
          // Retry the request with new token (increment retry count)
          return fetchApi<T>(endpoint, { ...options, __retryCount: retryCount + 1 })
        }
        // Refresh failed, clear auth and redirect
        clearAuthTokens()
        window.location.href = '/login'
      }

      throw error
    }

    // Handle empty responses
    const contentType = response.headers.get('content-type')
    if (contentType && contentType.includes('application/json')) {
      return response.json()
    }

    return {} as T
  } catch (err) {
    clearTimeout(timeoutId)

    // Handle timeout
    if (err instanceof Error && err.name === 'AbortError') {
      const error = new Error('Request timeout') as ApiError
      error.status = 408
      throw error
    }

    throw err
  }
}

/**
 * Attempt to refresh the access token
 */
async function tryRefreshToken(): Promise<boolean> {
  const refreshToken = localStorage.getItem('kcs_refresh_token')
  if (!refreshToken) return false

  try {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT)

    const response = await fetch(`${API_BASE_URL}/auth/refresh/`, {
      method: 'POST',
      signal: controller.signal,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh: refreshToken }),
    })

    clearTimeout(timeoutId)

    if (response.ok) {
      const data = await response.json()
      localStorage.setItem('kcs_access_token', data.access)
      if (data.refresh) {
        localStorage.setItem('kcs_refresh_token', data.refresh)
      }
      return true
    }
  } catch {
    // Refresh failed
  }

  return false
}

/**
 * Clear all auth tokens from localStorage
 */
export function clearAuthTokens(): void {
  localStorage.removeItem('kcs_access_token')
  localStorage.removeItem('kcs_refresh_token')
}

/**
 * Set auth tokens after successful login
 */
export function setAuthTokens(access: string, refresh: string): void {
  localStorage.setItem('kcs_access_token', access)
  localStorage.setItem('kcs_refresh_token', refresh)
}

/**
 * Check if user has valid access token
 */
export function hasAuthToken(): boolean {
  return !!localStorage.getItem('kcs_access_token')
}

// Convenience methods

export const api = {
  get: <T>(endpoint: string) => fetchApi<T>(endpoint, { method: 'GET' }),

  post: <T>(endpoint: string, data?: unknown) =>
    fetchApi<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    }),

  put: <T>(endpoint: string, data?: unknown) =>
    fetchApi<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    }),

  patch: <T>(endpoint: string, data?: unknown) =>
    fetchApi<T>(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    }),

  delete: <T>(endpoint: string) => fetchApi<T>(endpoint, { method: 'DELETE' }),
}

export default api

/**
 * Fetch wrapper with ETag caching support for GeoJSON endpoints
 * Sends If-None-Match header and returns cached data on 304
 */
export async function fetchWithETag<T>(endpoint: string, __retryCount = 0): Promise<T> {
  if (!endpoint.startsWith('/')) {
    throw new Error(`Invalid endpoint: must start with /`)
  }

  const token = localStorage.getItem('kcs_access_token')
  const cacheKey = endpoint
  const cached = etagCache.get(cacheKey)

  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT)

  try {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    }

    // Add If-None-Match header if we have a cached ETag
    if (cached?.etag) {
      headers['If-None-Match'] = cached.etag
    }

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'GET',
      signal: controller.signal,
      headers,
    })

    clearTimeout(timeoutId)

    // 304 Not Modified - return cached data
    if (response.status === 304 && cached) {
      return cached.data as T
    }

    if (!response.ok) {
      const error = new Error(`HTTP ${response.status}`) as ApiError
      error.status = response.status
      
      try {
        const errData = await response.json()
        error.data = errData
        if (errData && typeof errData.detail === 'string') {
          error.message = errData.detail
        } else if (errData && typeof errData.error === 'string') {
          error.message = errData.error
        }
      } catch {
        // Ignore JSON parse errors
      }

      // Handle token expiration - limit to 1 retry
      if (response.status === 401 && __retryCount < 1) {
        const refreshed = await tryRefreshToken()
        if (refreshed) {
          // Retry with new token
          return fetchWithETag<T>(endpoint, __retryCount + 1)
        }
        // Refresh failed, clear auth and redirect
        clearAuthTokens()
        window.location.href = '/login'
      }

      throw error
    }

    const data = await response.json()

    // Store ETag if present
    const etag = response.headers.get('ETag')
    if (etag) {
      etagCache.set(cacheKey, {
        etag,
        data,
        timestamp: Date.now(),
      })
    }

    return data as T
  } catch (err) {
    clearTimeout(timeoutId)

    if (err instanceof Error && err.name === 'AbortError') {
      const error = new Error('Request timeout') as ApiError
      error.status = 408
      throw error
    }

    throw err
  }
}
